<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'claims',
    path: '/claims',
    component: 'Claims',
    title: () => pgettext('Claims', 'Claims'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Claims',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const invoiceTypeBadgeClass = (...args) => {
    const [row] = args;
    const cc = $config.value.invoice.types.colorCodes[row.invoice_type];
    return `bg-${cc.bg} text-${cc.text}`;
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'invoice_number',
      name: $fn.value.pgettext('InvoicesView', 'Invoice nr'),
      type: 'command',
      formatter: [
        {
          name: 'link',
          href: '#',
          className: 'command-link',
        },
        {
          name: 'cellclass',
          value: (row) => ({
            'fw-bold': true,
            'bg-yellow-200': !row.locked,
            'bg-yellow-500 text-yellow-800': row.is_corrected,
            'bg-red-300 text-red-800': row.is_reversed,
          }),
        },
      ],
    },
    {
      field: 'invoice_type',
      name: $fn.value.pgettext('InvoicesView', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'InvoiceType',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            dot: true,
            pill: true,
            badgeClass: invoiceTypeBadgeClass,
          },
        ],
      },
    },
    {
      field: 'issuer',
      name: $fn.value.pgettext('Claims', 'Issuer'),
      formatter: {
        name: 'chain',
        formatterContext: 'row',
        formatters: [
          {
            name: 'getvalue',
            field: 'issuer.name',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'issuer.id',
            },
          },
        ],
      },
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('Claims', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'dunning_letters',
      name: $fn.value.pgettext('Claims', 'Dunning letters'),
      flags: {
        preventCellActivate: true,
      },
      type: 'command',
      formatter: {
        name: 'dunningLetterBadges',
      },
    },
    {
      field: 'uncollectible',
      name: $fn.value.pgettext('Claims', 'Uncollectible'),
      formatter: {
        name: 'bool',
        trueValue: $fn.value.gettext('Yes'),
      },
    },
    {
      field: 'days_past_due',
      name: $fn.value.pgettext('Claims', 'Days past due'),
      formatter: {
        name: 'overdueHighlight',
      },
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('Claims', 'Amount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'debt',
      name: $fn.value.pgettext('Claims', 'Claim'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'paid',
      name: $fn.value.pgettext('Claims', 'Paid'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'max_payment_date',
      name: $fn.value.pgettext('Claims', 'Date of last settlement'),
      type: 'date',
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('InvoicesView', 'Currency'),
    },
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('InvoicesView', 'Invoice date'),
    },
    {
      field: 'fulfillment_date',
      name: $fn.value.pgettext('InvoicesView', 'Fulfillment date'),
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('InvoicesView', 'Due date'),
    },
    {
      field: 'payment_method',
      name: $fn.value.pgettext('InvoicesView', 'Payment method'),
      formatter: {
        name: 'gettext',
        context: 'PaymentMethod',
        conversion: 'Sentence',
        filterInput: true,
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_claims');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('Claims', 'Claims'),
      icon: $config.value.claim.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_m989qeigc_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_mcip2ap0p_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_fraoijzdl_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'DunningLetter',
      props: {
        id: $event.data.element.dataset.value,
      },
    };
  };
  const inst_evh_2248226175642056_command_iwtr7zh2j_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'dunning_letters';
  };
  const act_2248226175642056_showModal_ybz56f2pp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_2fxcpdym4_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice_number';
  };
  const act_4615685607825208_showModal_slmds548r_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateDunningLetter',
      props: {
        items: selectedItems.value,
      },
    };
  };

  const act_7315092382398562_closeModal_m989qeigc =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_m989qeigc_getActionArgs,
    });
  const inst_evh_7315092382398562_close_tsxm9sody =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_m989qeigc,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_tsxm9sody],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_uaq5h7xlh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_vkzwx0e83 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_uaq5h7xlh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_vkzwx0e83],
    event: 'reload',
  });
  const act_2248226175642056_setData_mcip2ap0p = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_mcip2ap0p_getActionArgs,
    displayName: 'setSelectedItems',
  });
  const inst_evh_2248226175642056_selectedRowsChanged_5ukbt8jkx =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_mcip2ap0p,
      event: 'selectedRowsChanged',
      displayName: 'setSelectedItems',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_5ukbt8jkx],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_fraoijzdl =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_fraoijzdl_getActionArgs,
      displayName: 'showDunningLetter',
    });
  const inst_evh_2248226175642056_command_iwtr7zh2j =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_fraoijzdl,
      event: 'command',
      when: inst_evh_2248226175642056_command_iwtr7zh2j_getWhen,
      displayName: 'showDunningLetter',
    });
  const act_2248226175642056_showModal_ybz56f2pp =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_ybz56f2pp_getActionArgs,
      displayName: 'showInvoiceModal',
    });
  const inst_evh_2248226175642056_command_2fxcpdym4 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_ybz56f2pp,
      event: 'command',
      when: inst_evh_2248226175642056_command_2fxcpdym4_getWhen,
      displayName: 'showInvoiceModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_iwtr7zh2j,
      inst_evh_2248226175642056_command_2fxcpdym4,
    ],
    event: 'command',
  });
  const act_4615685607825208_showModal_slmds548r =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_slmds548r_getActionArgs,
      displayName: 'showCreateDunningLetter',
    });
  const inst_evh_4615685607825208_click_i1k69qsdf =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_slmds548r,
      event: 'click',
      displayName: 'showCreateDunningLetter',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_i1k69qsdf],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_tsxm9sody,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_vkzwx0e83,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_5ukbt8jkx,
    setSelectedItems: inst_evh_2248226175642056_selectedRowsChanged_5ukbt8jkx,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_iwtr7zh2j,
    showDunningLetter: inst_evh_2248226175642056_command_iwtr7zh2j,
    inst_evh_2248226175642056_command_2fxcpdym4,
    showInvoiceModal: inst_evh_2248226175642056_command_2fxcpdym4,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_click_i1k69qsdf,
    showCreateDunningLetter: inst_evh_4615685607825208_click_i1k69qsdf,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_tsxm9sody = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_tsxm9sody.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_vkzwx0e83 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_vkzwx0e83.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_5ukbt8jkx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_5ukbt8jkx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_iwtr7zh2j = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_iwtr7zh2j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_2fxcpdym4 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_2fxcpdym4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_i1k69qsdf = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_i1k69qsdf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Claims"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="claims"
      :headerTitle="documentTitle"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'claims')"
      footer-totals
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            icon="fa-cog"
            :text="gettext('Create dunning letters...')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            variant="primary"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/claims/claims.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
